<template>
  <div class="page-admin-user">

    <div class="page-admin-user__header">

      <div
        v-hide="!(
          store_admin_list.hasFetchOnce &&
          !companyListFix.isPending.value
        )"
        class="page-admin-user__subfilters"
      >

        <ui-wad class="font-weight-light font-size-90">
          <span
            v-if="total_list_count > 0"
          >
            <layout-misc-float :value="total_list_count" />
            {{
              total_list_count < 2 ?
                `résultat` :
                `résultats`
            }}
          </span>
          <span
            v-if="total_list_count === 0"
          >
            Aucun résultat
          </span>
        </ui-wad>

        <ui-button-link
          v-if="store_admin_list.anyFilterSet"
          class="font-size-90"
          :variant="UI.VARIANT.SECONDARY"
          underline="hover"
          @click="() => store_admin_list.resetFilters()"
        >
          <ui-icon
            name="filter_alt_off"
          />
          supprimer les filtres
         </ui-button-link>

      </div>

      <b-card
        :class="[
          'page-admin-user__filters',
          {
            'page-admin-user__filters--busy':
              !store_admin_list.hasFetchOnce ||
              companyListFix.isPending.value ||
              store_admin_list.isPending,
          },
        ]"
        body-class="p-1 d-flex justify-content-space-between"
      >

        <!-- Filters: first column -->
        <ui-wad class="d-flex align-items-center gap-1">

          <ui-form-inner-icon
            name="search"
            :size="UI.SIZE.SMALL"
          >
            <ui-form-input
              :value="store_admin_list.filters.search"
              placeholder="Rechercher"
              :size="UI.SIZE.SMALL"
              class="page-admin-user__filters__search"
              @update:model-value="debounchedSearchChange"
            />
          </ui-form-inner-icon>

          <ui-chip-select-multiple
            v-model="store_admin_list.filters.societe_id_list"
            :options="
              companyListFix.companyOptions.value satisfies ChipSelectMultipleOption<Company['societeId']>[]
            "
            hoistable
            searchable
          >
            Sociétés
          </ui-chip-select-multiple>

        </ui-wad>


        <!-- Filter: second column -->
        <ui-wad>
          <ui-tooltip-wrapper
            :status="
              hasACL(ACLS.PERMISSION_MANAGEMENT_CREATE) ?
                UI.STATUS.DISABLED :
                UI.STATUS.ENABLED
            "
            v-bind="{
              title: `Vous n'avez pas les droits pour effectuer cette action`,
              variant: UI.VARIANT.LIGHT,
            }"
            class="d-inline-block"
          >
            <layout-cta-sub
              component="ui-link-button"
              :variant="UI.VARIANT.PRIMARY"
              :status="
                hasACL(ACLS.PERMISSION_MANAGEMENT_CREATE) ?
                  UI.STATUS.ENABLED :
                  UI.STATUS.DISABLED
              "
              :route="invitationRoute"
            >
              <ui-icon
                name="person_add"
              />
              &nbsp;Inviter un nouvel utilisateur
            </layout-cta-sub>
          </ui-tooltip-wrapper>
        </ui-wad>

      </b-card>







    </div>

    <layout-nav-tab-menu
      v-model="selectedTabMenu"
      :items="tabMenuItems"
      @update:model-value="onRouteChangeIntent"
    >
      <template #right-side>
        <!-- @NOTE #PAR-22: leave out this feature for now -->
        <transition
          v-if="hasFetchOnce"
          appear
          appear-active-class="animate__animated animate__fadeIn ease-in-out a1"
        >
          <ui-wad class="d-flex align-items-center flex-direction-row-reverse flex-grow-1 mr-4">
            <ui-popover-wrapper
              :size="UI.SIZE.SMALL"
              placement="bottom"
              body-class="p-1.5"
              no-flip
              no-caret
            >
              <template #default>
                <ui-wad class="d-flex align-items-center">
                  <ui-caveat
                    :size="UI.SIZE.SMALL"
                    class="align-self-center font-weight-light line-height-0.9"
                  >
                    {{ paid_licence_count }} accès préparateur de paie utilisés
                    <ui-badge-light
                      class="ml-1 font-size-13px color-primary"
                      :variant="UI.VARIANT.PRIMARY"
                    >
                      <ui-icon
                        name="key"
                      />
                    </ui-badge-light>
                  </ui-caveat>

                  <ui-icon
                    name="info"
                    class="font-size-110 color-primary ml-0.5"
                  />

              </ui-wad>
              </template>

              <template #popover>
                <ui-wad class="font-weight-light font-size-90">
                  Contactez le service commercial GHS pour obtenir des accès supplémentaires
                </ui-wad>
              </template>

            </ui-popover-wrapper>

          </ui-wad>
        </transition>
      </template>
    </layout-nav-tab-menu>

    <div class="page-admin-user__main">

      <nuxt-page />

    </div>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/layout/members

$search-width: 328px

.page-admin-user
  $self: &

  &__header
    padding: ($layout-spacer-y * .75) $layout-spacer-x $layout-spacer-y
    background-color: $subheader-bg

  /////////////////////////////////

  &__subfilters
    display: flex
    gap: $card-spacer-x
    align-items: baseline
    padding-left: space-x(1.5)
    padding-right: space-x(1.5)
    margin-bottom: $layout-spacer-y * .25
    animation: fade-in 150ms ease-in-out

  /////////////////////////////////

  &__filters

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

    &__search
      width: $search-width

</style>

<script lang="ts">
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useDataOnlyNavigationContext } from '~/composables/layout/navigation'
import { getListActiveRoute, getListPendingRoute, getListDeletedRoute, getCreationRoute } from '~/stores/client/admin'
import { debounce, not, propOr, test } from '~/utils/lib/core/prelude'
import { TAB_MENU, useStore_client_admin_list } from '~/stores/client/admin/list'
import { extractRouteCompanyCode } from '~/stores/layout/navigation'
import { mapState, mapWritableState } from 'pinia'
import { useCompanyListFix } from '~/composables/parameters/company-list-fix'
import type { Company } from '~/stores/client/access'
import type { ChipSelectMultipleOption } from '~/components/ui/chip/SelectMultiple.vue'
import { useAccessControl } from '~/composables/layout/access-control'

const SEARCH_EXECUTION_DEBOUNCE_DELAY = 300

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.DATA_ONLY,
      menuItem: MENU_ITEM.PARAMETERS_USER_MANAGEMENT,
      middleware: function(to) {
        const needDefaultRedirection = test(
          /liste$/,
          to.path
        )

        if (not(needDefaultRedirection))
          return

        const structure_id :string = propOr('', 'dataId', to.params)
        const company_code = extractRouteCompanyCode(to)

        const defaultRoute = getListActiveRoute(structure_id, company_code)

        return navigateTo(defaultRoute, { replace: true })
      }
    })

    const navigationContext = useDataOnlyNavigationContext()

    useHead({
      title: `Gestion des utilisateurs — ${navigationContext.fromStructureLevel().structure.raison}`,
    })

    const companyListFix = useCompanyListFix()

    const company_code = extractRouteCompanyCode(useRoute())
    const invitationRoute = getCreationRoute(
      navigationContext.fromStructureLevel().structure.id,
      company_code
    )

    const store_admin_list = useStore_client_admin_list()

    return {
      ...navigationContext,
      companyListFix,
      store_admin_list,
      invitationRoute,
      ...useAccessControl(),
    }
  },
  data() {
    const tabMenuItems = [
      {
        id: TAB_MENU.ACTIVE_USER_LIST,
        text: `Utilisateurs actifs`,
      },
      {
        id: TAB_MENU.PENDING_INVITATION_LIST,
        text: `Invitations en attente`,
      },
    ]

    return {
      TAB_MENU,
      tabMenuItems,
      // Filters
      debounchedSearchChange: null as unknown as (_: string) => void, // (lazy)
    }
  },
  computed: {
    ...mapState(useStore_client_admin_list, [
      'paid_licence_count',
      'total_list_count',
      'hasFetchOnce',
    ]),
    ...mapWritableState(useStore_client_admin_list, [
      'selectedTabMenu',
    ]),
  },
  created() {
    this.companyListFix.hydrate()

    this.store_admin_list.resetPage()

    this.debounchedSearchChange = debounce(
      SEARCH_EXECUTION_DEBOUNCE_DELAY,
      value => this.store_admin_list.filters.search = value
    )
  },
  methods: {
    onRouteChangeIntent(tab_item) {
      const {
        structure: { id },
      } = this.fromStructureLevel()
      const company_code = extractRouteCompanyCode(this.$route)

      const route = (() => {
        switch (tab_item as TAB_MENU) {
          case TAB_MENU.ACTIVE_USER_LIST:
            return getListActiveRoute(id, company_code)
          case TAB_MENU.PENDING_INVITATION_LIST:
            return getListPendingRoute(id, company_code)
          case TAB_MENU.DELETED_USER_LIST:
            return getListDeletedRoute(id, company_code)
        }
      })()

      return navigateTo(route)
    },
  }
})
</script>
